import React from 'react';
import { isProduction } from 'utils/environment';
import type { QAComparison } from 'components/pages/address/public/address-page.types';

type ListingSourceType = 'go-search' | 'client-app-fallback';

interface Props {
  source?: string;
  qaComparison?: QAComparison;
  activeSource?: ListingSourceType;
  onTabChange?: (source: ListingSourceType) => void;
}

export default function ListingSourceBanner({ source, qaComparison, activeSource, onTabChange }: Props) {
  if (isProduction) return null;

  if (qaComparison && activeSource && onTabChange) {
    return <QASourceTabs qaComparison={qaComparison} activeSource={activeSource} onTabChange={onTabChange} />;
  }

  if (!source) return null;

  const isGoSearch = source === 'go-search';
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 99999,
      padding: '6px 12px',
      fontSize: '13px',
      fontWeight: 600,
      textAlign: 'center',
      color: '#fff',
      backgroundColor: isGoSearch ? '#16a34a' : '#ea580c',
      pointerEvents: 'none',
    }}>
      Listing source: {source}
    </div>
  );
}

function QASourceTabs({ qaComparison, activeSource, onTabChange }: {
  qaComparison: QAComparison;
  activeSource: ListingSourceType;
  onTabChange: (source: ListingSourceType) => void;
}) {
  const tabs: { key: ListingSourceType; label: string; error?: string }[] = [
    { key: 'go-search', label: 'go-search', error: qaComparison.goSearch.error },
    { key: 'client-app-fallback', label: 'client-app', error: qaComparison.clientApp.error },
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 99999,
      display: 'flex',
      justifyContent: 'center',
      gap: '2px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {tabs.map(tab => {
        const isActive = activeSource === tab.key;
        const hasError = !!tab.error;
        const isGoSearch = tab.key === 'go-search';

        let bgColor: string;
        if (isActive) {
          bgColor = isGoSearch ? '#16a34a' : '#ea580c';
        } else {
          bgColor = hasError ? '#6b7280' : '#9ca3af';
        }

        return (
          <button
            key={tab.key}
            onClick={() => !hasError && onTabChange(tab.key)}
            style={{
              padding: '6px 16px',
              fontSize: '12px',
              fontWeight: isActive ? 700 : 500,
              color: '#fff',
              backgroundColor: bgColor,
              border: 'none',
              borderRadius: '0 0 6px 6px',
              cursor: hasError ? 'not-allowed' : 'pointer',
              opacity: hasError && !isActive ? 0.7 : 1,
              transition: 'background-color 150ms',
            }}
            title={tab.error || undefined}
          >
            {tab.label}
            {hasError && ' ⚠'}
            {isActive && ' ●'}
          </button>
        );
      })}
    </div>
  );
}
